<script setup lang="ts">
import { Delta } from "@vueup/vue-quill";
import InfiniteLoading from "v3-infinite-loading";
import "v3-infinite-loading/lib/style.css";
import { PropType } from "vue";
import { RevisionEditHistory } from "~/classes/models/submitted-documents/revision-history-edit.model";
import { SubmittedDocumentsService } from "~/classes/models/submitted-documents/submitted-documents.service";
import { SheetController } from "~/components/Base/Sheet/controller";
import { useWordProcessorStore } from "~/components/Base/WordProcessor/useWordProcessorStore";

const props = defineProps({
  submittedDocumentId: {
    type: String,
    required: true,
  },
  revisionId: {
    type: String,
    required: true,
  },
  sheetController: {
    type: Object as PropType<SheetController>,
    required: true,
  },
});

const PAGE_SIZE = 20;
const DEFAULT_SHEET_WIDTH = "500px";
const PREVIEW_SHEET_WIDTH = "95%";
const previewInstanceId = `document-history-preview-${props.submittedDocumentId}`;

const editingHistory = ref<RevisionEditHistory[]>([]);
const selectedHistoryId = ref<string | undefined>();
const previewHistoryId = ref<string | undefined>();
const previewDelta = ref<any | undefined>(undefined);
const isPreviewOpen = ref(false);
const isLoading = ref(false);
const isLoadingMore = ref(false);
const isRestoring = ref(false);
const hasMore = ref(true);
const oldestLoadedTimestamp = ref<number | undefined>();

const submittedDocumentStore = useSubmittedDocumentStore(
  props.submittedDocumentId
);
const selfAssessmentStore = useDocumentSelfAssessmentStore(
  props.submittedDocumentId
);
const wordProcessorStore = useWordProcessorStore(props.submittedDocumentId);

const { revision, submittedDocument } = storeToRefs(submittedDocumentStore);
const { quillDelta, stepCriteriaAttributes } = storeToRefs(selfAssessmentStore);
const { appUser } = storeToRefs(useAppUserDataStore());
const { beginRestore, endRestore } = useDocumentEditingHistoryRestoreState(
  props.submittedDocumentId
);

const previewHistoryItem = computed(() => {
  return editingHistory.value.find((item) => item.id == previewHistoryId.value);
});

const previewTitle = computed(() => {
  if (!previewHistoryItem.value) return "";
  return useDateTimeFormatter(previewHistoryItem.value.lastUpdatedTimestamp);
});

const showPreviewPane = computed(() => {
  return isPreviewOpen.value == true && previewHistoryItem.value != undefined;
});

const isRestoreDisabled = computed(() => {
  return previewHistoryItem.value == undefined || isRestoring.value == true;
});

const previewHeader = computed(() => {
  return previewHistoryItem.value?.headers?.[0] ?? revision.value?.headers?.[0];
});

const customAttributors = computed(() => {
  return stepCriteriaAttributes.value ?? [];
});

const appendHistoryRecords = (records: RevisionEditHistory[]) => {
  const existingIds = new Set(editingHistory.value.map((item) => item.id));
  const nextRecords = records.filter((item) => {
    return item.id == undefined || existingIds.has(item.id) != true;
  });

  editingHistory.value = [...editingHistory.value, ...nextRecords];

  const timestamps = editingHistory.value
    .map((item) => item.lastUpdatedTimestamp)
    .filter((value) => typeof value == "number");

  oldestLoadedTimestamp.value =
    timestamps.length > 0 ? Math.min(...timestamps) : undefined;
};

const fetchHistoryPage = async ({
  isInitialLoad = false,
}: {
  isInitialLoad?: boolean;
} = {}) => {
  if (isInitialLoad) {
    if (isLoading.value) return;
    isLoading.value = true;
  } else {
    if (isLoadingMore.value || hasMore.value != true) return;
    isLoadingMore.value = true;
  }

  try {
    const records = await SubmittedDocumentsService.fetchEditingHistoryPage({
      submittedDocumentId: props.submittedDocumentId,
      revisionId: props.revisionId,
      limitCount: PAGE_SIZE,
      beforeTimestamp: isInitialLoad ? undefined : oldestLoadedTimestamp.value,
    });

    appendHistoryRecords(records);
    hasMore.value = records.length >= PAGE_SIZE;
  } catch (error) {
    useBaseToast("Oops! Unable to load editing history.", "error");
  } finally {
    if (isInitialLoad) {
      isLoading.value = false;
    } else {
      isLoadingMore.value = false;
    }
  }
};

const loadMore = async () => {
  await fetchHistoryPage();
};

const cloneOps = (ops: any) => {
  if (ops == undefined) return undefined;

  return JSON.parse(JSON.stringify(ops));
};

const areOpsEqual = (left: any, right: any) => {
  return JSON.stringify(left) === JSON.stringify(right);
};

const areHeadersEqual = (
  left: RevisionEditHistory["headers"],
  right: RevisionEditHistory["headers"]
) => {
  return JSON.stringify(left ?? []) === JSON.stringify(right ?? []);
};

const createHistorySnapshot = async (
  studentSubmissionOps: any,
  headers?: RevisionEditHistory["headers"]
) => {
  const timestamp = Date.now();
  const userId =
    useCurrentUID() ?? submittedDocument.value?.userId ?? "unknown-user";

  const documentEditingHistory = new RevisionEditHistory({
    documentId: props.submittedDocumentId,
    revisionId: props.revisionId,
    studentSubmissionOps,
    headers: headers ?? revision.value?.headers,
    lastUpdatedTimestamp: timestamp,
    editedByUserId: userId,
    editedByUserName:
      appUser.value?.displayName ?? submittedDocument.value?.studentName,
  });

  await documentEditingHistory.save();

  return documentEditingHistory;
};

const openPreview = (historyItem: RevisionEditHistory) => {
  const previewOps = cloneOps(historyItem.studentSubmissionOps);

  if (previewOps == undefined) {
    useBaseToast("Oops! This history item cannot be previewed.", "error");
    return;
  }

  selectedHistoryId.value = historyItem.id;
  previewHistoryId.value = historyItem.id;
  previewDelta.value = new Delta(previewOps);
  isPreviewOpen.value = true;
  props.sheetController.setWidth(PREVIEW_SHEET_WIDTH);
  props.sheetController.setMaxWidth(PREVIEW_SHEET_WIDTH);
};

const closePreview = () => {
  isPreviewOpen.value = false;
  previewHistoryId.value = undefined;
  previewDelta.value = undefined;
  props.sheetController.resetSize();
};

const restorePreviewHistory = async () => {
  if (!revision.value) {
    useBaseToast("Oops! The revision could not be found.", "error");
    return;
  }

  if (revision.value.studentSubmissionOps == undefined) {
    useBaseToast("Oops! The current revision cannot be restored yet.", "error");
    return;
  }

  if (previewHistoryItem.value?.studentSubmissionOps == undefined) {
    useBaseToast("Oops! Preview a version before restoring it.", "error");
    return;
  }

  const currentOps = cloneOps(revision.value.studentSubmissionOps);
  const selectedOps = cloneOps(previewHistoryItem.value.studentSubmissionOps);
  const currentHeaders = revision.value.headers
    ? JSON.parse(JSON.stringify(revision.value.headers))
    : undefined;
  const selectedHeaders = previewHistoryItem.value.headers
    ? JSON.parse(JSON.stringify(previewHistoryItem.value.headers))
    : undefined;

  if (
    areOpsEqual(currentOps, selectedOps) &&
    areHeadersEqual(currentHeaders, selectedHeaders)
  ) {
    useBaseToast("This version already matches the current draft.", "error");
    return;
  }

  isRestoring.value = true;
  beginRestore();

  try {
    const backupRecord = await createHistorySnapshot(currentOps, currentHeaders);

    editingHistory.value = [backupRecord, ...editingHistory.value].filter(
      (item, index, records) => {
        return records.findIndex((record) => record.id == item.id) == index;
      }
    );

    revision.value.studentSubmissionOps = selectedOps;
    revision.value.headers = selectedHeaders;
    revision.value.lastUpdatedTimestamp = Date.now();

    const restoredDelta = new Delta(selectedOps);

    quillDelta.value = restoredDelta as any;
    await submittedDocumentStore.saveRevision(revision.value, true);

    const quill = wordProcessorStore.getQuillInstance();
    if (quill) {
      quill.setContents(restoredDelta, "api");
      quill.history.clear();
    }

    previewDelta.value = new Delta(cloneOps(selectedOps));

    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    useBaseToast("Revision restored.", "success");
    await props.sheetController.close();
  } catch (error) {
    useBaseToast("Oops! Unable to restore this version.", "error");
  } finally {
    endRestore();
    isRestoring.value = false;
  }
};

onMounted(() => {
  props.sheetController.setWidth(DEFAULT_SHEET_WIDTH);
  fetchHistoryPage({ isInitialLoad: true });
});

onUnmounted(() => {
  props.sheetController.resetSize();
});
</script>

<template>
  <div class="flex h-full min-h-[32rem] flex-col overflow-hidden bg-white">
    <div class="border-b border-gray-200 px-4 py-4 pt-0">
      <h3 class="text-lg font-semibold text-gray-900">Editing History</h3>
      <p class="mt-1 text-sm text-gray-500">
        Click an earlier version to preview it, then restore it into the current
        draft.
      </p>
    </div>

    <div
      class="flex min-h-0 flex-1 overflow-hidden"
      :class="{
        'flex-col': showPreviewPane != true,
        'flex-row': showPreviewPane,
      }"
    >
      <div
        class="flex min-h-0 flex-col overflow-hidden border-gray-200"
        :class="{
          'w-full': showPreviewPane != true,
          'w-[360px] shrink-0 border-r': showPreviewPane,
        }"
      >
        <div class="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          <div v-if="isLoading" class="flex justify-center py-8">
            <BaseLoading />
          </div>

          <BaseEmptyListMessage
            v-else-if="editingHistory.length == 0"
            class="mx-2 mt-2"
          >
            No editing history yet.
          </BaseEmptyListMessage>

          <div v-else class="flex flex-col gap-2">
            <button
              v-for="historyItem in editingHistory"
              :key="historyItem.id ?? historyItem.lastUpdatedTimestamp"
              type="button"
              class="group mb-1 w-full text-left transition-all duration-200"
              @click="openPreview(historyItem)"
            >
              <div
                class="flex items-center gap-3 overflow-hidden rounded-lg border px-3 py-2.5 transition-all duration-200"
                :class="[
                  selectedHistoryId == historyItem.id
                    ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20'
                    : 'border-transparent bg-transparent hover:bg-gray-50',
                ]"
              >
                <div
                  class="flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors"
                  :class="[
                    selectedHistoryId == historyItem.id
                      ? 'bg-white text-primary shadow-sm'
                      : 'bg-gray-100 text-gray-500 group-hover:bg-white group-hover:text-primary group-hover:shadow-sm',
                  ]"
                >
                  <BaseIconHistory class="h-4 w-4" />
                </div>

                <div class="min-w-0 flex-1">
                  <h3
                    class="truncate text-sm font-medium transition-colors"
                    :class="{
                      'text-primary': selectedHistoryId == historyItem.id,
                      'text-gray-900': selectedHistoryId != historyItem.id,
                    }"
                  >
                    {{ useDateTimeFormatter(historyItem.lastUpdatedTimestamp) }}
                  </h3>

                  <div v-if="historyItem.editedByUserName" class="mt-0.5">
                    <span class="truncate text-xs text-gray-500">
                      {{ historyItem.editedByUserName }}
                    </span>
                  </div>
                </div>

                <div
                  v-if="selectedHistoryId == historyItem.id"
                  class="flex items-center text-primary"
                >
                  <div class="h-1.5 w-1.5 rounded-full bg-primary"></div>
                </div>
              </div>
            </button>

            <InfiniteLoading
              v-if="hasMore"
              class="invisible"
              @infinite="loadMore"
            />

            <div v-if="isLoadingMore" class="flex justify-center py-2">
              <BaseLoading />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="showPreviewPane"
        class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-gray-50"
      >
        <div
          class="flex items-center justify-between border-b border-gray-200 bg-white px-4 py-3 shadow-sm"
        >
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              @click="closePreview"
            >
              <BaseIconClose class="h-5 w-5" />
            </button>
            <div>
              <h3 class="text-sm font-medium text-gray-900">
                Previewing Version
              </h3>
              <p class="text-xs text-gray-500">
                {{ previewTitle }}
              </p>
            </div>
          </div>

          <div class="w-[130px]">
            <BaseButton
              :disabled="isRestoreDisabled"
              :show-spinner="isRestoring"
              @click="restorePreviewHistory"
            >
              Restore
            </BaseButton>
          </div>
        </div>

        <div class="min-h-0 flex-1 overflow-auto bg-gray-100/50 px-8 pt-8 pb-0">
          <div
            class="mx-auto flex min-h-full max-w-[816px] flex-col bg-white shadow-sm rounded-t"
          >
            <BaseWordProcessorContainer
              v-if="previewDelta"
              :instance-id="previewInstanceId"
              :model-value="previewDelta"
              :header="
                previewHistoryItem?.headers?.[0] ?? revision?.headers?.[0]
              "
              :read-only="true"
              :custom-attributors="customAttributors"
              class="min-h-0 flex-1 preview-container"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.preview-container.word-processor-container) {
  height: auto !important;
  overflow: visible !important;
}

:deep(.preview-container .page-container) {
  padding-top: 48px !important;
  padding-bottom: 48px !important;
  overflow: visible !important;
}

:deep(.preview-container .ql-editor) {
  padding: 0px 48px !important;
  overflow: visible !important;
}

/* Remove the default editor frame styling from BaseWordProcessorContainer */
:deep(.preview-container.word-processor-container > div) {
  box-shadow: none !important;
  border: none !important;
  border-radius: 0 !important;
  overflow: visible !important;
}
</style>
